import { cn } from "@/lib/utils";
import {
  LATEST_CHROME_EXTENSION_VERSION_LABEL,
  selectClubImportState,
  selectShouldWarnExtensionOutdated,
} from "@/redux/clubImport/clubImportSlice";
import { useTranslation } from "@repo/shared/i18n";
import { AlertTriangle } from "lucide-react";
import { isMacOs } from "react-device-detect";
import { useSelector } from "react-redux";

// Strong but non-blocking nudge onto the latest extension.
// Renders nothing unless an outdated version was actually reported.
// Names the installed version — a warning that can't say what you're on
// reads like boilerplate and gets clicked past.
// Copy says "browser", not Chrome: the gate is window.chrome, so Edge,
// Brave and Opera users see this too.
// Extensions update on browser restart, so that hint leads and the store
// link is the fallback, same as the tutorial's ExtensionOutdatedBanner.
export const ExtensionUpdateWarning = ({
  className,
}: {
  className?: string;
}) => {
  const { t } = useTranslation("clubImport");
  const needsUpdate = useSelector(selectShouldWarnExtensionOutdated);
  const { extensionVersion } = useSelector(selectClubImportState);

  if (!needsUpdate) return null;

  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-2.5 rounded-xl p-3.5",
        "border border-amber-500/70 bg-amber-500/10",
        className,
      )}
    >
      <AlertTriangle
        className="mt-0.5 size-5 shrink-0 text-amber-400"
        aria-hidden
      />
      <div className="flex flex-col gap-1.5">
        <h3 className="text-sm font-bold text-white">
          {t("extensionUpdateWarning.title")}
        </h3>
        <p className="text-sm leading-5 text-white">
          {t("extensionUpdateWarning.body", {
            current: extensionVersion,
            required: LATEST_CHROME_EXTENSION_VERSION_LABEL,
          })}
        </p>
        {/* Hint and link share a line; the link wraps with the text. */}
        <p className="text-sm leading-5 text-dark-secondary">
          {isMacOs
            ? t("extensionUpdateWarning.macHint")
            : t("extensionUpdateWarning.windowsHint")}{" "}
        </p>
      </div>
    </div>
  );
};
